import type { PointerEvent } from 'react';
import React, { useCallback } from 'react';

import { ChatIconButton } from '../../components/chat/ChatIconButton';
import { Tooltip } from '../../components/GraphModal/Tooltip';
import { sharedUploadLogic } from '../../editor/_old/upload';
import type { GraphEditorInstance } from '../../editor/editor-model';
import { usePermissions } from '../../hooks/usePermissions';
import { useWebsite } from '../../hooks/useWebsite';
import { ReactComponent as ImageIcon } from '../../icons/icon-image.inline.svg';
import { handleError } from '../../utils/common-utils';
import { isMobile } from '../../utils/web';
import { EmojisPopover } from '../Content/MessageContent/formatters/EmojisPopover';
import { FormatTextHintButton } from './formatting-options/FormatTextHintButton';

const noop = () => true;

const forceDisableFileUpload = false;

export const ThreadEditorToolbar: React.FC<{
  baseId?: string;
  editorRef?: React.MutableRefObject<GraphEditorInstance | null>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ baseId, editorRef, setLoading }) => {
  const { authUserId } = usePermissions();
  const { graphdebate = {} } = useWebsite();
  const { edition = {} } = graphdebate ?? {};
  const { upload: canUploadRaw, emoticons: canEmot } = edition ?? {};
  const canUpload = canUploadRaw && authUserId;

  const onAddEmoji = useCallback(
    emoji => {
      if (!editorRef?.current?.addContent) return;

      editorRef.current.addContent(emoji);
    },
    [editorRef],
  );

  const upload = useCallback(
    async (file: File) => {
      if (!canUpload) {
        return;
      }

      setLoading(true);
      try {
        const { uploadedFile } = await sharedUploadLogic(file);
        if (uploadedFile) {
          editorRef?.current?.addContent(uploadedFile);
        }
      } catch (error) {
        handleError(error);
        // The generic error handling in packages/graphdebate/src/components/Layout/MainLayout.tsx => applyErrorHandler is enough to notify the user.
      } finally {
        setLoading(false);
      }
    },
    [canUpload, editorRef, setLoading],
  );

  const inputFieldKey = `uploadPicture-${baseId}`;

  const preventDefault = useCallback(
    (e: PointerEvent<HTMLDivElement | HTMLLabelElement>) => e.preventDefault(),
    [],
  );

  const isMob = isMobile();

  return (
    <div className="flex text-gray-medium space-x-1 items-center">
      {canEmot && (
        <EmojisPopover onAdd={onAddEmoji}>
          <ChatIconButton iconClassName="icon-emoticon text-lg sm:text-md" onClick={noop} />
        </EmojisPopover>
      )}
      <FormatTextHintButton />
      {!isMob && canUpload && !forceDisableFileUpload && (
        <>
          <input
            type="file"
            key={inputFieldKey}
            id={inputFieldKey}
            className="invisible"
            accept=".png,.jpg,.jpeg,.gif"
            onChange={event => {
              upload(event.target.files?.[0] as File);
            }}
          />
          <Tooltip content="Cliquez ici pour ajouter une image">
            <label
              htmlFor={inputFieldKey}
              className="flex items-center justify-center w-11 h-11 cursor-pointer hover:text-gray-medium"
              onPointerDown={preventDefault}
            >
              <ImageIcon className="w-8 h-8 sm:w-6 sm:h-6" />
            </label>
          </Tooltip>
        </>
      )}
    </div>
  );
};
